// src/components/admin/AdminConfig.jsx
// Content & Configuration — Phase 5 — Premium Enterprise Redesign

import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';

// ── Helpers ───────────────────────────────────────────────────
const fmtDate = d => d
  ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const ANNOUNCEMENT_TYPES = [
  { value: 'info',     label: 'Info',     color: '#60a5fa',  bg: 'rgba(96,165,250,.10)'   },
  { value: 'success',  label: 'Success',  color: '#2ecc8a',  bg: 'rgba(46,204,138,.10)'   },
  { value: 'warning',  label: 'Warning',  color: '#fbbf24',  bg: 'rgba(251,191,36,.10)'   },
  { value: 'critical', label: 'Critical', color: '#d94f4f',  bg: 'rgba(217,79,79,.10)'    },
];

const CATEGORIES = ['forex', 'crypto', 'stocks', 'indices', 'commodities'];

const typeStyle   = type => ANNOUNCEMENT_TYPES.find(t => t.value === type) || ANNOUNCEMENT_TYPES[0];
const catColor    = c => ({ forex: 'var(--gold)', crypto: '#60a5fa', stocks: '#2ecc8a', indices: '#a78bfa', commodities: '#f472b6' }[c] || 'var(--gray)');

// ── Shared style constants — matches EmailBlast.jsx surface style ─
// Removed backdropFilter/blur — that caused the dark-blue tint mismatch
const GLASS_SURFACE = {
  background: 'var(--dark2)',
};

// ── Spinner ───────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ padding: 56, textAlign: 'center', color: 'rgba(247,243,234,.3)', fontSize: 12, letterSpacing: '.06em', textTransform: 'uppercase' }}>
      <div style={{ fontSize: 22, marginBottom: 10, opacity: .4 }}>◌</div>Loading…
    </div>
  );
}

// ── Tag pill ──────────────────────────────────────────────────
function Tag({ text, color, bg, border }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 10px', borderRadius: 999,
      fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
      background: bg || `${color}18`,
      color,
      border: `1px solid ${border || color + '30'}`,
    }}>{text}</span>
  );
}

// ── Stat summary card ─────────────────────────────────────────
function StatCard({ label, value, sub, accent, icon }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...GLASS_SURFACE,
        border: `1px solid ${hov ? accent + '40' : 'rgba(255,255,255,.07)'}`,
        borderRadius: 12,
        padding: '18px 20px',
        display: 'flex', flexDirection: 'column', gap: 6,
        boxShadow: hov
          ? `0 12px 32px rgba(0,0,0,.35), 0 0 0 1px ${accent}20, inset 0 1px 0 rgba(255,255,255,.06)`
          : '0 2px 12px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.04)',
        transform: hov ? 'translateY(-2px)' : 'none',
        transition: 'all .18s ease',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* ambient glow */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${accent}, ${accent}50, transparent)`, opacity: hov ? 1 : .5, transition: 'opacity .18s' }}/>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(247,243,234,.35)' }}>{label}</span>
        <span style={{ fontSize: 14, opacity: .45 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '.02em', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'rgba(247,243,234,.35)', letterSpacing: '.03em' }}>{sub}</div>}
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        background: 'var(--dark2)',
        border: '1px solid rgba(255,255,255,.1)',
        borderRadius: 14, padding: '28px 32px', maxWidth: 380, width: '90%',
        boxShadow: '0 32px 64px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08)',
      }}>
        <div style={{ fontSize: 14, color: 'var(--off-white)', lineHeight: 1.75, marginBottom: 24 }}>{message}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px 0', background: 'rgba(217,79,79,.12)', border: '1px solid rgba(217,79,79,.35)', color: '#d94f4f', fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 999 }}>Delete</button>
          <button onClick={onCancel}  style={{ flex: 1, padding: '10px 0', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', color: 'var(--gray)', fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 999 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Input helpers (legacy, used by InstrumentsSection) ────────
const inputStyle = { width: '100%', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.09)', color: 'var(--white)', fontSize: 13, padding: '9px 12px', outline: 'none', fontFamily: "'Inter',sans-serif", borderRadius: 8, boxSizing: 'border-box', transition: 'border-color .15s' };
const labelStyle = { fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'rgba(247,243,234,.35)', display: 'block', marginBottom: 6 };

// ── TYPE_ICONS ────────────────────────────────────────────────
const TYPE_ICONS = { info: '◉', success: '✓', warning: '⚠', critical: '✕' };

// ── Hover-aware pill action button ───────────────────────────
function ActionPill({ label, color, bg, border, hoverBg, onClick, small }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: small ? '4px 12px' : '6px 14px',
        background: hov ? (hoverBg || bg) : bg,
        border: `1px solid ${border}`,
        color,
        fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
        textTransform: 'uppercase', cursor: 'pointer',
        fontFamily: "'Inter',sans-serif",
        borderRadius: 999,
        transform: hov ? 'translateY(-1px)' : 'none',
        boxShadow: hov ? `0 4px 14px ${border}` : 'none',
        transition: 'all .15s ease',
        whiteSpace: 'nowrap',
      }}
    >{label}</button>
  );
}

// ── Gold-focus premium input ──────────────────────────────────
function PremiumInput({ label, value, onChange, placeholder, textarea, rows }) {
  const [focused, setFocused] = useState(false);
  const base = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,.035)',
    border: `1px solid ${focused ? 'rgba(212,168,67,.5)' : 'rgba(255,255,255,.09)'}`,
    boxShadow: focused
      ? '0 0 0 3px rgba(212,168,67,.09), inset 0 1px 3px rgba(0,0,0,.35)'
      : 'inset 0 1px 3px rgba(0,0,0,.25)',
    color: 'var(--white)', fontSize: 13, lineHeight: 1.6,
    padding: textarea ? '11px 14px' : '10px 14px',
    outline: 'none', fontFamily: "'Inter',sans-serif",
    borderRadius: 9,
    transition: 'border-color .18s, box-shadow .18s',
    resize: textarea ? 'vertical' : undefined,
    minHeight: textarea ? (rows === 'lg' ? 90 : 68) : undefined,
  };
  return (
    <div>
      {label && <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(247,243,234,.35)', display: 'block', marginBottom: 6 }}>{label}</label>}
      {textarea
        ? <textarea style={base} value={value} onChange={onChange} placeholder={placeholder} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        : <input    style={base} value={value} onChange={onChange} placeholder={placeholder} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
      }
    </div>
  );
}

// ── Type segmented control ────────────────────────────────────
function TypeSegment({ value, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
      {ANNOUNCEMENT_TYPES.map(t => {
        const active = value === t.value;
        return (
          <button
            key={t.value}
            onClick={() => onChange(t.value)}
            style={{
              padding: '7px 0',
              background: active ? t.bg : 'rgba(255,255,255,.02)',
              border: `1px solid ${active ? t.color + '60' : 'rgba(255,255,255,.07)'}`,
              color: active ? t.color : 'rgba(247,243,234,.3)',
              fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
              textTransform: 'uppercase', cursor: 'pointer',
              fontFamily: "'Inter',sans-serif", borderRadius: 8,
              boxShadow: active ? `0 0 14px ${t.color}20` : 'none',
              transition: 'all .15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            }}
          >
            <span style={{ fontSize: 10, opacity: active ? 1 : .45 }}>{TYPE_ICONS[t.value]}</span>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Toggle switch ─────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
      <div
        onClick={onChange}
        style={{
          width: 40, height: 22, borderRadius: 11,
          background: value ? 'var(--green)' : 'rgba(255,255,255,.08)',
          border: `1px solid ${value ? 'rgba(46,204,138,.4)' : 'rgba(255,255,255,.1)'}`,
          position: 'relative', transition: 'all .2s', cursor: 'pointer', flexShrink: 0,
          boxShadow: value ? '0 0 10px rgba(46,204,138,.3)' : 'none',
        }}
      >
        <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 14, height: 14, borderRadius: '50%', background: 'var(--white)', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.4)' }} />
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: value ? 'var(--off-white)' : 'var(--gray)' }}>{label}</div>
        <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 1 }}>{value ? 'Visible to all users' : 'Hidden from users'}</div>
      </div>
    </label>
  );
}

// ════════════════════════════════════════════════════════════
//  USER-FACING ANNOUNCEMENT BANNER (exact replica)
// ════════════════════════════════════════════════════════════
function AnnouncementBanner({ type, title, body }) {
  const ts = typeStyle(type);
  if (!title && !body) return (
    <div style={{ padding: '18px 20px', background: 'rgba(255,255,255,.02)', border: '1px dashed rgba(255,255,255,.07)', borderRadius: 10, textAlign: 'center' }}>
      <div style={{ fontSize: 20, marginBottom: 6, opacity: .25 }}>◎</div>
      <div style={{ fontSize: 11, color: 'var(--gray)' }}>Preview appears as you type</div>
    </div>
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 0,
      background: ts.bg,
      border: `1px solid ${ts.color}30`,
      borderLeft: `3px solid ${ts.color}`,
      borderRadius: 8,
      overflow: 'hidden',
      boxShadow: `0 2px 12px ${ts.color}10`,
      transition: 'all .2s',
    }}>
      <div style={{ padding: '12px 16px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: title ? 5 : 0 }}>
          <span style={{ fontSize: 11, color: ts.color }}>{TYPE_ICONS[type]}</span>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', color: ts.color, textTransform: 'uppercase' }}>{type}</span>
        </div>
        {title && <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', marginBottom: body ? 4 : 0, lineHeight: 1.4 }}>{title}</div>}
        {body  && <div style={{ fontSize: 12, color: 'rgba(247,243,234,.75)', lineHeight: 1.65 }}>{body}</div>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  ANNOUNCEMENT HISTORY CARD
// ════════════════════════════════════════════════════════════
function AnnouncementCard({ a, onEdit, onDelete, onToggle }) {
  const [hov, setHov] = useState(false);
  const ts = typeStyle(a.type);
  const sc = { accent: ts.color, glow: ts.bg };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', overflow: 'hidden',
        ...GLASS_SURFACE,
        border: `1px solid ${hov && a.active ? sc.accent + '45' : a.active ? sc.accent + '22' : 'rgba(255,255,255,.06)'}`,
        borderRadius: 12,
        boxShadow: hov
          ? `0 12px 36px rgba(0,0,0,.4), 0 0 0 1px ${sc.accent}22, inset 0 1px 0 rgba(255,255,255,.07)`
          : '0 2px 10px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.04)',
        transform: hov ? 'translateY(-2px)' : 'none',
        opacity: a.active ? 1 : .5,
        transition: 'all .18s ease',
      }}
    >
      {/* Left accent strip */}
      <div style={{
        width: 4, flexShrink: 0,
        background: a.active
          ? `linear-gradient(180deg, ${sc.accent}, ${sc.accent}50)`
          : 'rgba(255,255,255,.07)',
        boxShadow: hov && a.active ? `2px 0 16px ${sc.accent}40` : 'none',
        transition: 'box-shadow .18s',
      }} />

      {/* Card body */}
      <div style={{ flex: 1, padding: '16px 20px' }}>
        {/* Top row: title + badges + date */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', lineHeight: 1.3 }}>{a.title}</span>
            <Tag text={a.type.toUpperCase()} color={ts.color} bg={ts.bg} border={ts.color + '35'} />
            <Tag
              text={a.active ? '● Active' : '○ Inactive'}
              color={a.active ? '#2ecc8a' : 'var(--gray)'}
              bg={a.active ? 'rgba(46,204,138,.08)' : 'rgba(148,163,184,.06)'}
              border={a.active ? 'rgba(46,204,138,.22)' : 'rgba(148,163,184,.12)'}
            />
          </div>
          <span style={{ fontSize: 10, color: 'rgba(247,243,234,.3)', whiteSpace: 'nowrap', flexShrink: 0, marginTop: 2, letterSpacing: '.02em' }}>{fmtDate(a.createdAt)}</span>
        </div>

        {/* Body text */}
        <div style={{ fontSize: 12, color: 'rgba(247,243,234,.55)', lineHeight: 1.7, marginBottom: 14 }}>{a.body}</div>

        {/* Bottom action row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, borderTop: '1px solid rgba(255,255,255,.04)', paddingTop: 12 }}>
          <ActionPill
            label={a.active ? 'Deactivate' : 'Activate'}
            color={a.active ? '#fbbf24' : '#2ecc8a'}
            bg={a.active ? 'rgba(251,191,36,.06)' : 'rgba(46,204,138,.06)'}
            border={a.active ? 'rgba(251,191,36,.22)' : 'rgba(46,204,138,.22)'}
            hoverBg={a.active ? 'rgba(251,191,36,.12)' : 'rgba(46,204,138,.12)'}
            onClick={() => onToggle(a)}
          />
          <ActionPill
            label="Edit"
            color="var(--gold)"
            bg="rgba(212,168,67,.06)"
            border="rgba(212,168,67,.22)"
            hoverBg="rgba(212,168,67,.12)"
            onClick={() => onEdit(a)}
          />
          <ActionPill
            label="Delete"
            color="#d94f4f"
            bg="rgba(217,79,79,.06)"
            border="rgba(217,79,79,.2)"
            hoverBg="rgba(217,79,79,.12)"
            onClick={() => onDelete(a.id)}
          />
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  ANNOUNCEMENTS SECTION
// ════════════════════════════════════════════════════════════
function AnnouncementsSection({ setOnNewClick }) {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ title: '', body: '', type: 'info', active: true });
  const [error,   setError]   = useState(null);

  useEffect(() => {
    adminApi.getAnnouncements()
      .then(d => setList(d.announcements || []))
      .finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setForm({ title: '', body: '', type: 'info', active: true });
    setEditing('new');
    setError(null);
  };

  useEffect(() => { if (setOnNewClick) setOnNewClick(() => openNew); }, []);

  const openEdit = a => {
    setForm({ title: a.title, body: a.body, type: a.type, active: a.active });
    setEditing(a);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) { setError('Title and body are required.'); return; }
    setSaving(true); setError(null);
    try {
      if (editing === 'new') {
        const { announcement } = await adminApi.createAnnouncement(form);
        setList(l => [announcement, ...l]);
      } else {
        const { announcement } = await adminApi.updateAnnouncement(editing.id, form);
        setList(l => l.map(a => a.id === announcement.id ? announcement : a));
      }
      setEditing(null);
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const handleDelete = id => {
    setConfirm({
      message: 'Delete this announcement? It will be removed immediately for all users.',
      onConfirm: async () => {
        setConfirm(null);
        await adminApi.deleteAnnouncement(id);
        setList(l => l.filter(a => a.id !== id));
      },
    });
  };

  const handleToggleActive = async a => {
    try {
      const { announcement } = await adminApi.updateAnnouncement(a.id, { active: !a.active });
      setList(l => l.map(x => x.id === announcement.id ? announcement : x));
    } catch {}
  };

  // Stats
  const totalActive   = list.filter(a => a.active).length;
  const totalInactive = list.length - totalActive;
  const typeBreakdown = ANNOUNCEMENT_TYPES.map(t => ({ ...t, count: list.filter(a => a.type === t.value).length })).filter(t => t.count > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

      {/* ── STATS ROW ─────────────────────────────────────────── */}
      {!loading && list.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <StatCard label="Total"    value={list.length}    sub="all announcements"  accent="var(--gold)"  icon="📢" />
          <StatCard label="Active"   value={totalActive}    sub="visible to users"   accent="#2ecc8a"      icon="●" />
          <StatCard label="Inactive" value={totalInactive}  sub="hidden from users"  accent="rgba(148,163,184,.5)" icon="○" />
          <StatCard
            label="Types"
            value={typeBreakdown.length}
            sub={typeBreakdown.map(t => `${t.count} ${t.label.toLowerCase()}`).join(' · ')}
            accent="#a78bfa"
            icon="◈"
          />
        </div>
      )}

      {/* ── COMPOSER CARD ─────────────────────────────────────── */}
      {editing && (
        <div style={{
          ...GLASS_SURFACE,
          border: '1px solid rgba(255,255,255,.09)',
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.07)',
        }}>
          {/* Top gold accent line */}
          <div style={{ height: 2, background: 'linear-gradient(90deg, var(--gold), rgba(212,168,67,.3) 60%, transparent)' }} />

          {/* Card header */}
          <div style={{ padding: '16px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 3 }}>
                <span style={{ fontSize: 14 }}>📢</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.01em' }}>
                  {editing === 'new' ? 'Create Announcement' : 'Edit Announcement'}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(247,243,234,.35)', lineHeight: 1.5 }}>
                Broadcast important information to all users instantly.
              </p>
            </div>
            <button
              onClick={() => setEditing(null)}
              style={{ background: 'transparent', border: 'none', color: 'rgba(247,243,234,.3)', fontSize: 17, cursor: 'pointer', padding: 4, transition: 'color .15s', lineHeight: 1 }}
              onMouseEnter={e => e.target.style.color = 'var(--off-white)'}
              onMouseLeave={e => e.target.style.color = 'rgba(247,243,234,.3)'}
            >✕</button>
          </div>

          {/* 2-col layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 22, padding: '16px 24px' }}>

            {/* Left: title + body */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <PremiumInput
                label="Title"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Scheduled maintenance on Sunday…"
              />
              <PremiumInput
                label="Message"
                textarea
                rows="lg"
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write the announcement message shown to all users…"
              />
            </div>

            {/* Right: type + toggle + preview */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(247,243,234,.35)', display: 'block', marginBottom: 8 }}>Announcement Type</label>
                <TypeSegment value={form.type} onChange={v => setForm(f => ({ ...f, type: v }))} />
              </div>

              <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 9 }}>
                <Toggle
                  value={form.active}
                  onChange={() => setForm(f => ({ ...f, active: !f.active }))}
                  label="Publish immediately"
                />
              </div>

              {/* Live preview — exact user-facing replica */}
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(247,243,234,.35)', display: 'block', marginBottom: 8 }}>
                  Live Preview
                  <span style={{ marginLeft: 6, fontSize: 8, color: 'rgba(247,243,234,.2)', fontWeight: 500, letterSpacing: '.04em', textTransform: 'none' }}>— shown to users exactly as below</span>
                </label>
                <AnnouncementBanner type={form.type} title={form.title} body={form.body} />
              </div>
            </div>
          </div>

          {/* Footer action bar */}
          <div style={{
            padding: '12px 24px',
            borderTop: '1px solid rgba(255,255,255,.05)',
            background: 'rgba(0,0,0,.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              {error && (
                <span style={{ fontSize: 11, color: '#d94f4f', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>⚠</span> {error}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setEditing(null)}
                style={{ padding: '8px 20px', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', color: 'var(--gray)', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 999, letterSpacing: '.06em', textTransform: 'uppercase', transition: 'all .15s' }}
                onMouseEnter={e => { e.target.style.borderColor = 'rgba(255,255,255,.2)'; e.target.style.color = 'var(--off-white)'; }}
                onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,.1)'; e.target.style.color = 'var(--gray)'; }}
              >Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 24px',
                  background: saving ? 'rgba(212,168,67,.08)' : 'linear-gradient(135deg, rgba(212,168,67,.22), rgba(212,168,67,.1))',
                  border: '1px solid rgba(212,168,67,.45)',
                  color: 'var(--gold)', fontSize: 10, fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: "'Inter',sans-serif", borderRadius: 999,
                  letterSpacing: '.06em', textTransform: 'uppercase',
                  opacity: saving ? .6 : 1,
                  boxShadow: saving ? 'none' : '0 0 18px rgba(212,168,67,.18)',
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { if (!saving) { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 20px rgba(212,168,67,.28)'; } }}
                onMouseLeave={e => { e.target.style.transform = 'none'; e.target.style.boxShadow = '0 0 18px rgba(212,168,67,.18)'; }}
              >
                {saving ? 'Saving…' : 'Save Announcement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY SECTION ───────────────────────────────────── */}
      {loading ? <Spinner /> : (
        <div>
          {/* Section header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 15, opacity: .55 }}>📜</span>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.01em' }}>Existing Announcements</div>
                <div style={{ fontSize: 11, color: 'rgba(247,243,234,.35)', marginTop: 2 }}>
                  {list.length} announcement{list.length !== 1 ? 's' : ''} · {list.filter(a => a.active).length} active
                </div>
              </div>
            </div>
            {list.length > 0 && (
              <div style={{ display: 'flex', gap: 5 }}>
                {ANNOUNCEMENT_TYPES.map(t => {
                  const count = list.filter(a => a.type === t.value).length;
                  if (!count) return null;
                  return (
                    <Tag key={t.value} text={`${t.label} ${count}`} color={t.color} bg={t.bg} border={t.color + '28'} />
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(255,255,255,.07), transparent)', marginBottom: 16 }} />

          {!list.length && !editing ? (
            <div style={{
              padding: '56px 24px', textAlign: 'center',
              background: 'var(--dark2)',
              border: '1px dashed rgba(255,255,255,.07)',
              borderRadius: 14,
            }}>
              <div style={{ fontSize: 28, marginBottom: 10, opacity: .2 }}>📢</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--off-white)', marginBottom: 6 }}>No announcements yet</div>
              <div style={{ fontSize: 12, color: 'var(--gray)' }}>Use the composer above to broadcast to all users.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {list.map(a => (
                <AnnouncementCard
                  key={a.id}
                  a={a}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggleActive}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  INSTRUMENTS SECTION — premium upgrade to match Announcements
// ════════════════════════════════════════════════════════════
function InstrumentsSection() {
  const [list,      setList]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [confirm,   setConfirm]   = useState(null);
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState({ symbol: '', name: '', category: 'forex', active: true });
  const [error,     setError]     = useState(null);
  const [catFilter, setCatFilter] = useState('all');
  const [formFocus, setFormFocus] = useState({});

  useEffect(() => {
    adminApi.getInstruments()
      .then(d => setList(d.instruments || []))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!form.symbol.trim() || !form.name.trim()) { setError('Symbol and name are required.'); return; }
    setSaving(true); setError(null);
    try {
      const { instrument } = await adminApi.createInstrument(form);
      setList(l => [...l, instrument].sort((a, b) => a.symbol.localeCompare(b.symbol)));
      setShowForm(false);
      setForm({ symbol: '', name: '', category: 'forex', active: true });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleToggle = async inst => {
    try {
      const { instrument } = await adminApi.updateInstrument(inst.id, { active: !inst.active });
      setList(l => l.map(i => i.id === instrument.id ? instrument : i));
    } catch {}
  };

  const handleDelete = id => {
    setConfirm({
      message: 'Remove this instrument? Users will no longer be able to select it for new trades.',
      onConfirm: async () => {
        setConfirm(null);
        await adminApi.deleteInstrument(id);
        setList(l => l.filter(i => i.id !== id));
      },
    });
  };

  const filtered  = catFilter === 'all' ? list : list.filter(i => i.category === catFilter);
  const counts    = CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: list.filter(i => i.category === c).length }), {});
  const totalActive = list.filter(i => i.active).length;

  // Premium input for instruments form
  const InstInput = ({ field, label, placeholder, transform }) => {
    const focused = !!formFocus[field];
    return (
      <div>
        <label style={labelStyle}>{label}</label>
        <input
          style={{
            ...inputStyle,
            border: `1px solid ${focused ? 'rgba(212,168,67,.5)' : 'rgba(255,255,255,.09)'}`,
            boxShadow: focused ? '0 0 0 3px rgba(212,168,67,.09)' : 'none',
            ...(transform ? { textTransform: transform } : {}),
          }}
          value={form[field]}
          onChange={e => setForm(f => ({ ...f, [field]: transform ? e.target.value.toUpperCase() : e.target.value }))}
          placeholder={placeholder}
          onFocus={() => setFormFocus(f => ({ ...f, [field]: true }))}
          onBlur={() => setFormFocus(f => ({ ...f, [field]: false }))}
        />
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── STATS ROW ─────────────────────────────────────────── */}
      {!loading && list.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <StatCard label="Total Instruments" value={list.length}    sub="all markets"          accent="var(--gold)"  icon="📊" />
          <StatCard label="Active"            value={totalActive}    sub="tradeable by users"   accent="#2ecc8a"      icon="●" />
          <StatCard label="Inactive"          value={list.length - totalActive} sub="hidden from users" accent="rgba(148,163,184,.5)" icon="○" />
          <StatCard label="Categories"        value={CATEGORIES.filter(c => counts[c] > 0).length} sub={CATEGORIES.filter(c => counts[c]).map(c => `${counts[c]} ${c}`).join(' · ')} accent="#a78bfa" icon="◈" />
        </div>
      )}

      {/* ── CONTROLS ROW ──────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        {/* Category filter pills */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {['all', ...CATEGORIES].map(c => {
            const active = catFilter === c;
            return (
              <button
                key={c}
                onClick={() => setCatFilter(c)}
                style={{
                  padding: '5px 13px',
                  background: active ? 'rgba(212,168,67,.1)' : 'rgba(255,255,255,.03)',
                  border: `1px solid ${active ? 'rgba(212,168,67,.4)' : 'rgba(255,255,255,.08)'}`,
                  color: active ? 'var(--gold)' : 'rgba(247,243,234,.4)',
                  fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
                  cursor: 'pointer', fontFamily: "'Inter',sans-serif",
                  textTransform: 'capitalize', borderRadius: 999,
                  boxShadow: active ? '0 0 12px rgba(212,168,67,.15)' : 'none',
                  transition: 'all .15s',
                }}
              >
                {c === 'all' ? `All  ${list.length}` : `${c}  ${counts[c] || 0}`}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => { setShowForm(true); setError(null); }}
          style={{
            padding: '7px 18px',
            background: 'linear-gradient(135deg, rgba(212,168,67,.18), rgba(212,168,67,.08))',
            border: '1px solid rgba(212,168,67,.38)',
            color: 'var(--gold)', fontSize: 10, fontWeight: 700,
            cursor: 'pointer', fontFamily: "'Inter',sans-serif",
            borderRadius: 999, letterSpacing: '.06em', textTransform: 'uppercase',
            boxShadow: '0 0 16px rgba(212,168,67,.12)',
            transition: 'all .15s',
          }}
          onMouseEnter={e => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 20px rgba(212,168,67,.22)'; }}
          onMouseLeave={e => { e.target.style.transform = 'none'; e.target.style.boxShadow = '0 0 16px rgba(212,168,67,.12)'; }}
        >+ Add Instrument</button>
      </div>

      {/* ── ADD FORM ──────────────────────────────────────────── */}
      {showForm && (
        <div style={{
          ...GLASS_SURFACE,
          border: '1px solid rgba(255,255,255,.09)',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.07)',
        }}>
          <div style={{ height: 2, background: 'linear-gradient(90deg, var(--gold), rgba(212,168,67,.3) 60%, transparent)' }} />
          <div style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)', marginBottom: 14, letterSpacing: '-.01em' }}>Add Instrument</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12, marginBottom: 14 }}>
              <InstInput field="symbol"   label="Symbol"   placeholder="EURUSD" transform="toUpperCase" />
              <InstInput field="name"     label="Name"     placeholder="Euro / US Dollar" />
              <div>
                <label style={labelStyle}>Category</label>
                <select
                  style={{
                    ...inputStyle, cursor: 'pointer',
                    border: `1px solid ${formFocus.category ? 'rgba(212,168,67,.5)' : 'rgba(255,255,255,.09)'}`,
                    boxShadow: formFocus.category ? '0 0 0 3px rgba(212,168,67,.09)' : 'none',
                  }}
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  onFocus={() => setFormFocus(f => ({ ...f, category: true }))}
                  onBlur={() => setFormFocus(f => ({ ...f, category: false }))}
                >
                  {CATEGORIES.map(c => <option key={c} value={c} style={{ background: 'var(--dark3)', textTransform: 'capitalize' }}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>{error && <span style={{ fontSize: 11, color: '#d94f4f', display: 'flex', alignItems: 'center', gap: 5 }}><span>⚠</span>{error}</span>}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setShowForm(false)}
                  style={{ padding: '7px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', color: 'var(--gray)', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 999, letterSpacing: '.05em', textTransform: 'uppercase' }}
                  onMouseEnter={e => { e.target.style.borderColor = 'rgba(255,255,255,.2)'; e.target.style.color = 'var(--off-white)'; }}
                  onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,.1)'; e.target.style.color = 'var(--gray)'; }}
                >Cancel</button>
                <button
                  onClick={handleAdd}
                  disabled={saving}
                  style={{ padding: '7px 20px', background: 'linear-gradient(135deg, rgba(212,168,67,.2), rgba(212,168,67,.09))', border: '1px solid rgba(212,168,67,.4)', color: 'var(--gold)', fontSize: 10, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 999, letterSpacing: '.05em', textTransform: 'uppercase', opacity: saving ? .6 : 1, boxShadow: '0 0 14px rgba(212,168,67,.15)', transition: 'all .15s' }}
                >{saving ? 'Adding…' : 'Add Instrument'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TABLE ─────────────────────────────────────────────── */}
      {loading ? <Spinner /> : (
        <div style={{
          ...GLASS_SURFACE,
          border: '1px solid rgba(255,255,255,.07)',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.05)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Symbol', 'Name', 'Category', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '11px 16px', fontSize: 9, letterSpacing: '.09em', textTransform: 'uppercase', color: 'rgba(247,243,234,.3)', fontWeight: 700, background: 'rgba(0,0,0,.2)', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,.05)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inst, idx) => (
                <InstrumentRow
                  key={inst.id}
                  inst={inst}
                  isLast={idx === filtered.length - 1}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  catColor={catColor}
                />
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={5} style={{ padding: '40px 24px', textAlign: 'center', color: 'rgba(247,243,234,.3)', fontSize: 12 }}>
                    No instruments in this category.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ── Instrument table row with hover ──────────────────────────
function InstrumentRow({ inst, isLast, onToggle, onDelete, catColor }) {
  const [hov, setHov] = useState(false);
  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,.04)',
        opacity: inst.active ? 1 : .45,
        background: hov ? 'rgba(255,255,255,.025)' : 'transparent',
        transition: 'background .15s',
      }}
    >
      <td style={{ padding: '11px 16px' }}>
        <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, color: catColor(inst.category), letterSpacing: '.04em' }}>{inst.symbol}</span>
      </td>
      <td style={{ padding: '11px 16px', fontSize: 12, color: 'rgba(247,243,234,.7)' }}>{inst.name}</td>
      <td style={{ padding: '11px 16px' }}>
        <Tag text={inst.category.toUpperCase()} color={catColor(inst.category)} />
      </td>
      <td style={{ padding: '11px 16px' }}>
        {inst.active
          ? <Tag text="ACTIVE"   color="#2ecc8a" bg="rgba(46,204,138,.08)"  border="rgba(46,204,138,.25)" />
          : <Tag text="INACTIVE" color="var(--gray)" bg="rgba(148,163,184,.06)" border="rgba(148,163,184,.15)" />
        }
      </td>
      <td style={{ padding: '11px 16px' }}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <ActionPill
            small
            label={inst.active ? 'Disable' : 'Enable'}
            color={inst.active ? '#fbbf24' : '#2ecc8a'}
            bg={inst.active ? 'rgba(251,191,36,.06)' : 'rgba(46,204,138,.06)'}
            border={inst.active ? 'rgba(251,191,36,.22)' : 'rgba(46,204,138,.22)'}
            hoverBg={inst.active ? 'rgba(251,191,36,.12)' : 'rgba(46,204,138,.12)'}
            onClick={() => onToggle(inst)}
          />
          <ActionPill
            small
            label="Remove"
            color="#d94f4f"
            bg="rgba(217,79,79,.06)"
            border="rgba(217,79,79,.2)"
            hoverBg="rgba(217,79,79,.12)"
            onClick={() => onDelete(inst.id)}
          />
        </div>
      </td>
    </tr>
  );
}

// ════════════════════════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════════════════════════
export default function AdminConfig() {
  const [tab, setTab] = useState('announcements');
  const [openNewAnn, setOpenNewAnn] = useState(null);

  return (
    <div style={{ padding: '18px 22px 72px', animation: 'pageIn .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Tab bar ─────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        ...GLASS_SURFACE,
        border: '1px solid rgba(255,255,255,.07)',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.05)',
      }}>
        <div style={{ display: 'flex' }}>
          {[['announcements', '📢', 'Announcements'], ['instruments', '📊', 'Instruments']].map(([v, icon, label]) => {
            const active = tab === v;
            return (
              <button
                key={v}
                onClick={() => setTab(v)}
                style={{
                  padding: '13px 22px',
                  background: active ? 'rgba(212,168,67,.08)' : 'transparent',
                  border: 'none',
                  borderRight: '1px solid rgba(255,255,255,.05)',
                  borderBottom: active ? '2px solid var(--gold)' : '2px solid transparent',
                  color: active ? 'var(--gold)' : 'rgba(247,243,234,.4)',
                  fontSize: 12, fontWeight: active ? 700 : 500,
                  cursor: 'pointer', fontFamily: "'Inter',sans-serif",
                  letterSpacing: '.02em', transition: 'all .15s',
                  display: 'flex', alignItems: 'center', gap: 7,
                }}
              >
                <span style={{ fontSize: 13, opacity: active ? 1 : .5 }}>{icon}</span>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Announcements tab ──────────────────────────────── */}
      {tab === 'announcements' && (
        <div style={{ maxWidth: 1200 }}>
          {/* Page header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, gap: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.02em', marginBottom: 5 }}>
                Announcements
              </h2>
              <p style={{ margin: 0, fontSize: 13, color: 'rgba(247,243,234,.4)', lineHeight: 1.5 }}>
                Broadcast messages displayed as banners to all logged-in users.
              </p>
            </div>
            <button
              onClick={() => openNewAnn?.()}
              style={{
                padding: '9px 22px', flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(212,168,67,.2), rgba(212,168,67,.09))',
                border: '1px solid rgba(212,168,67,.4)',
                color: 'var(--gold)', fontSize: 10, fontWeight: 700,
                cursor: 'pointer', fontFamily: "'Inter',sans-serif",
                borderRadius: 999, letterSpacing: '.06em', textTransform: 'uppercase',
                boxShadow: '0 0 18px rgba(212,168,67,.14)',
                transition: 'all .15s',
              }}
              onMouseEnter={e => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 22px rgba(212,168,67,.25)'; }}
              onMouseLeave={e => { e.target.style.transform = 'none'; e.target.style.boxShadow = '0 0 18px rgba(212,168,67,.14)'; }}
            >
              + New Announcement
            </button>
          </div>
          <AnnouncementsSection setOnNewClick={setOpenNewAnn} />
        </div>
      )}

      {/* ── Instruments tab ────────────────────────────────── */}
      {tab === 'instruments' && (
        <div style={{ maxWidth: 1200 }}>
          {/* Page header */}
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--white)', letterSpacing: '-.02em', marginBottom: 5 }}>
              Instruments
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: 'rgba(247,243,234,.4)', lineHeight: 1.5 }}>
              Manage which markets users can select when logging trades.
            </p>
          </div>
          <InstrumentsSection />
        </div>
      )}
    </div>
  );
}